/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class PopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.reopenDismissKey = `${this.id}-reopen-dismissed`;
        this.reopenBadgeWrap = null;
        this.reopenBadge = null;
        this.reopenBadgeClose = null;
        this.reopenBadgeClickHandler = null;
        this.reopenBadgeCloseHandler = null;

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.querySelector('.alert')) {
            this.open();
          } else if (this.dataset.trigger === 'delay') {
            setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          }
        }

        this.initReopenBadge();
      }

      /**
       * Triggers when the web component is removed from the DOM
       */
      disconnectedCallback() {
        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }

        if (this.reopenBadge && this.reopenBadgeClickHandler) {
          this.reopenBadge.removeEventListener('click', this.reopenBadgeClickHandler);
        }
        if (this.reopenBadgeClose && this.reopenBadgeCloseHandler) {
          this.reopenBadgeClose.removeEventListener('click', this.reopenBadgeCloseHandler);
        }
        if (this.reopenBadgeWrap) {
          this.reopenBadgeWrap.remove();
        }
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'close' events on the modal.
       */
      close() {
        super.close();
        setCookie(this.cookie, true, parseInt(this.dataset.dismissDays, 10));
        this.showReopenBadge();
      }

      open(opener) {
        super.open(opener);
        sessionStorage.removeItem(this.reopenDismissKey);
        this.hideReopenBadge();
      }

      initReopenBadge() {
        const reopenEnabled = this.dataset.showReopenBadge !== 'false';
        if (!reopenEnabled) return;

        const badgeWrapId = `${this.id}-reopen-badge-wrap`;
        const existingWrap = document.getElementById(badgeWrapId);
        this.reopenBadgeWrap = existingWrap || document.createElement('div');

        if (!existingWrap) {
          this.reopenBadgeWrap.id = badgeWrapId;
          this.reopenBadgeWrap.className = 'popup-reopen-badge-wrap';

          this.reopenBadge = document.createElement('button');
          this.reopenBadge.type = 'button';
          this.reopenBadge.className = 'popup-reopen-badge';
          this.reopenBadge.setAttribute('aria-label', 'Reopen newsletter popup');
          this.reopenBadge.textContent = this.dataset.reopenBadgeText || '10% Off';

          this.reopenBadgeClose = document.createElement('button');
          this.reopenBadgeClose.type = 'button';
          this.reopenBadgeClose.className = 'popup-reopen-badge-close';
          this.reopenBadgeClose.setAttribute('aria-label', 'Hide reopen bubble');
          this.reopenBadgeClose.textContent = '×';

          this.reopenBadgeWrap.appendChild(this.reopenBadge);
          this.reopenBadgeWrap.appendChild(this.reopenBadgeClose);
          document.body.appendChild(this.reopenBadgeWrap);
        } else {
          this.reopenBadge = this.reopenBadgeWrap.querySelector('.popup-reopen-badge');
          this.reopenBadgeClose = this.reopenBadgeWrap.querySelector('.popup-reopen-badge-close');
        }

        if (!this.reopenBadge) return;
        this.reopenBadgeClickHandler = () => {
          sessionStorage.removeItem(this.reopenDismissKey);
          this.open(this.reopenBadge);
        };
        this.reopenBadge.addEventListener('click', this.reopenBadgeClickHandler);

        if (this.reopenBadgeClose) {
          this.reopenBadgeCloseHandler = (evt) => {
            evt.stopPropagation();
            sessionStorage.setItem(this.reopenDismissKey, '1');
            this.hideReopenBadge();
          };
          this.reopenBadgeClose.addEventListener('click', this.reopenBadgeCloseHandler);
        }

        if (getCookie(this.cookie) && (this.dataset.showOnMobile === 'true' || theme.mediaMatches.md)) {
          this.showReopenBadge();
        }
      }

      showReopenBadge() {
        if (!this.reopenBadgeWrap) return;
        if (this.dataset.showOnMobile !== 'true' && !theme.mediaMatches.md) return;
        if (sessionStorage.getItem(this.reopenDismissKey) === '1') return;
        this.reopenBadgeWrap.classList.add('is-visible');
      }

      hideReopenBadge() {
        if (!this.reopenBadgeWrap) return;
        this.reopenBadgeWrap.classList.remove('is-visible');
      }
    }

    customElements.define('pop-up', PopUp);
  });
}
